/**
 * Light / dark / system theme, persisted under `wehi_theme`.
 *
 * The same key and the same class-flipping logic run twice: once inline in
 * index.html before first paint (so the app never flashes the wrong palette),
 * and once here for the in-app toggle. Dark stays the default — a stored value
 * is only written when the user picks one.
 */

export type ThemeMode = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_KEY = 'wehi_theme';

export function readStoredTheme(): ThemeMode {
  try {
    const raw = localStorage.getItem(THEME_KEY);
    if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
  } catch {
    /* private mode / storage disabled */
  }
  return 'dark';
}

export function writeStoredTheme(mode: ThemeMode): void {
  try {
    localStorage.setItem(THEME_KEY, mode);
  } catch {
    /* ignore */
  }
}

export function systemTheme(): ResolvedTheme {
  return typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-color-scheme: light)').matches
    ? 'light'
    : 'dark';
}

export function resolveTheme(mode: ThemeMode): ResolvedTheme {
  return mode === 'system' ? systemTheme() : mode;
}

/** Flip the `.dark` class and `color-scheme` so native controls follow too. */
export function applyTheme(resolved: ResolvedTheme): void {
  const root = document.documentElement;
  root.classList.toggle('dark', resolved === 'dark');
  root.style.colorScheme = resolved;
  document
    .querySelector('meta[name="color-scheme"]')
    ?.setAttribute('content', resolved);
}
